'use client';
import React, { useState, useMemo } from 'react';
import {
  Search,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Edit2,
  Trash2,
  Eye,
  ChevronLeft,
  ChevronRight,
  Filter,
  Columns,
} from 'lucide-react';
import { ATTENDANCE_RECORDS, type AttendanceRecord } from '@/lib/mockData';
import { AttendanceBadge, DeportmentBadge } from '@/components/ui/StatusBadge';
import EmptyState from '@/components/ui/EmptyState';
import { SkeletonTableRow } from '@/components/ui/LoadingSkeleton';

type SortKey = keyof AttendanceRecord;
type SortDir = 'asc' | 'desc' | null;

const PAGE_SIZE_OPTIONS = [10, 20, 50];

// Columns that are hidden on mobile by default (collapsible)
const SECONDARY_COLS = new Set(['department', 'checkOut', 'duration', 'notes', 'enteredBy']);

export default function AttendanceRecordsTable() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading] = useState(false);
  const [showSecondary, setShowSecondary] = useState(false);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const filtered = useMemo(() => {
    let data = [...ATTENDANCE_RECORDS];
    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter(
        (r) =>
          r.teacherName.toLowerCase().includes(q) ||
          r.department.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== 'All') {
      data = data.filter((r) => r.status === statusFilter);
    }
    if (sortKey && sortDir) {
      data.sort((a, b) => {
        const av = a[sortKey] ?? '';
        const bv = b[sortKey] ?? '';
        const cmp = String(av).localeCompare(String(bv));
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return data;
  }, [search, statusFilter, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : d === 'desc' ? null : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  }

  function SortIcon({ col }: { col: SortKey }) {
    if (sortKey !== col) return <ChevronsUpDown size={12} className="text-muted-foreground/50" />;
    if (sortDir === 'asc') return <ChevronUp size={12} className="text-primary" />;
    if (sortDir === 'desc') return <ChevronDown size={12} className="text-primary" />;
    return <ChevronsUpDown size={12} className="text-muted-foreground/50" />;
  }

  const COLUMNS: { key: SortKey; label: string; sortable: boolean }[] = [
    { key: 'teacherName', label: 'Teacher', sortable: true },
    { key: 'department', label: 'Department', sortable: true },
    { key: 'date', label: 'Date', sortable: true },
    { key: 'status', label: 'Status', sortable: true },
    { key: 'checkIn', label: 'Check-In', sortable: true },
    { key: 'checkOut', label: 'Check-Out', sortable: false },
    { key: 'duration', label: 'Duration', sortable: false },
    { key: 'deportment', label: 'Deportment', sortable: true },
    { key: 'notes', label: 'Notes', sortable: false },
    { key: 'enteredBy', label: 'Entered By', sortable: false },
  ];

  const visibleColumns = COLUMNS.filter(
    (col) => !SECONDARY_COLS.has(col.key) || showSecondary
  );

  return (
    <div className="bg-card border border-border rounded-xl">
      {/* Table Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 sm:px-5 py-4 border-b border-border">
        <div>
          <h2 className="text-base font-600 text-foreground" style={{ fontWeight: 600 }}>
            Attendance Records
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {filtered.length} record{filtered.length !== 1 ? 's' : ''} — May 2026
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 sm:flex-none">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search teacher…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-8 pr-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring w-full sm:w-44"
            />
          </div>

          {/* Status Filter */}
          <div className="relative flex items-center gap-1.5">
            <Filter size={13} className="text-muted-foreground" />
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="text-sm border border-border rounded-lg bg-background text-foreground px-2 py-2 focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {['All', 'Present', 'Absent', 'Late', 'Excused', 'On Leave'].map((s) => (
                <option key={`filter-${s}`} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {/* Toggle secondary columns — visible on md+ */}
          <button
            type="button"
            onClick={() => setShowSecondary((v) => !v)}
            className={`hidden md:flex items-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-500 transition-colors ${
              showSecondary
                ? 'border-primary bg-primary/10 text-primary' :'border-border bg-card text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
            style={{ fontWeight: 500 }}
            title="Toggle secondary columns"
          >
            <Columns size={13} />
            {showSecondary ? 'Less' : 'More'}
          </button>
        </div>
      </div>

      {/* ── DESKTOP / TABLET TABLE (md+) ── */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              {visibleColumns.map((col) => (
                <th
                  key={`th-${col.key}`}
                  className={`px-4 py-3 text-left text-xs font-600 text-muted-foreground uppercase tracking-wide whitespace-nowrap ${col.sortable ? 'cursor-pointer hover:text-foreground select-none' : ''}`}
                  style={{ fontWeight: 600 }}
                  onClick={col.sortable ? () => handleSort(col.key) : undefined}
                >
                  <div className="flex items-center gap-1">
                    {col.label}
                    {col.sortable && <SortIcon col={col.key} />}
                  </div>
                </th>
              ))}
              <th className="px-4 py-3 text-left text-xs font-600 text-muted-foreground uppercase tracking-wide" style={{ fontWeight: 600 }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <SkeletonTableRow key={`skel-row-${i + 1}`} cols={visibleColumns.length + 1} />
              ))
            ) : paginated.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length + 1}>
                  <EmptyState
                    title="No attendance records found"
                    description="No records match your current search or filter."
                  />
                </td>
              </tr>
            ) : (
              paginated.map((record, idx) => (
                <tr
                  key={record.id}
                  className={`border-b border-border last:border-0 hover:bg-muted/40 transition-colors duration-100 ${idx % 2 === 1 ? 'bg-muted/20' : ''}`}
                >
                  <td className="px-4 py-3 font-500 text-foreground whitespace-nowrap" style={{ fontWeight: 500 }}>
                    {record.teacherName}
                  </td>
                  {showSecondary && (
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap text-xs">
                      {record.department}
                    </td>
                  )}
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap tabular-nums text-xs">
                    {record.date}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <AttendanceBadge status={record.status} />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap tabular-nums text-xs">
                    {record.checkIn ? (
                      <span className={
                        record.checkIn > '07:30' ? 'text-red-600 font-600'
                          : record.checkIn > '07:15' ? 'text-amber-600 font-500' : 'text-green-600 font-500'
                      } style={{ fontWeight: record.checkIn > '07:15' ? 600 : 500 }}>
                        {record.checkIn}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  {showSecondary && (
                    <td className="px-4 py-3 whitespace-nowrap tabular-nums text-xs text-muted-foreground">
                      {record.checkOut ?? '—'}
                    </td>
                  )}
                  {showSecondary && (
                    <td className="px-4 py-3 whitespace-nowrap tabular-nums text-xs text-muted-foreground">
                      {record.duration ?? '—'}
                    </td>
                  )}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <DeportmentBadge status={record.deportment} />
                  </td>
                  {showSecondary && (
                    <td className="px-4 py-3 text-xs text-muted-foreground max-w-[180px] truncate" title={record.notes}>
                      {record.notes || '—'}
                    </td>
                  )}
                  {showSecondary && (
                    <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                      {record.enteredBy}
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <RecordActions record={record} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── MOBILE STACKED CARDS (< md) ── */}
      <div className="md:hidden divide-y divide-border">
        {loading ? (
          <div className="p-4 space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={`m-skel-${i}`} className="h-20 rounded-lg bg-muted animate-pulse" />
            ))}
          </div>
        ) : paginated.length === 0 ? (
          <EmptyState
            title="No attendance records found"
            description="No records match your current search or filter."
          />
        ) : (
          paginated.map((record) => {
            const isExpanded = expandedRow === record.id;
            return (
              <div key={record.id} className="px-4 py-3">
                {/* Primary row */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-600 text-foreground truncate" style={{ fontWeight: 600 }}>
                      {record.teacherName}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">{record.department} · {record.date}</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <AttendanceBadge status={record.status} />
                    <button
                      type="button"
                      onClick={() => setExpandedRow(isExpanded ? null : record.id)}
                      className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                    >
                      {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                  </div>
                </div>

                {/* Inline key metrics */}
                <div className="flex items-center gap-3 mt-2">
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] text-muted-foreground uppercase tracking-wide">In</span>
                    <span className={`text-xs tabular-nums font-500 ${
                      record.checkIn && record.checkIn > '07:30' ? 'text-red-600'
                        : record.checkIn && record.checkIn > '07:15' ? 'text-amber-600' : 'text-green-600'
                    }`} style={{ fontWeight: 500 }}>
                      {record.checkIn ?? '—'}
                    </span>
                  </div>
                  <DeportmentBadge status={record.deportment} />
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="mt-3 pt-3 border-t border-border grid grid-cols-2 gap-x-4 gap-y-2">
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Check-Out</p>
                      <p className="text-xs tabular-nums text-foreground mt-0.5">{record.checkOut ?? '—'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Duration</p>
                      <p className="text-xs tabular-nums text-foreground mt-0.5">{record.duration ?? '—'}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Notes</p>
                      <p className="text-xs text-foreground mt-0.5">{record.notes || '—'}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Entered By</p>
                      <p className="text-xs text-foreground mt-0.5">{record.enteredBy}</p>
                    </div>
                    <div className="col-span-2 flex items-center gap-1 pt-1">
                      <RecordActions record={record} />
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 sm:px-5 py-4 border-t border-border">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>Rows:</span>
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            className="border border-border rounded px-1.5 py-1 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          >
            {PAGE_SIZE_OPTIONS.map((s) => (
              <option key={`ps-${s}`} value={s}>{s}</option>
            ))}
          </select>
          <span>
            {Math.min((page - 1) * pageSize + 1, filtered.length)}–
            {Math.min(page * pageSize, filtered.length)} of {filtered.length}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="p-1.5 rounded-lg border border-border text-muted-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft size={14} />
          </button>
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={`page-${i + 1}`}
              type="button"
              onClick={() => setPage(i + 1)}
              className={`min-w-8 h-8 text-xs rounded-lg border transition-colors ${
                page === i + 1
                  ? 'bg-primary text-primary-foreground border-primary font-600'
                  : 'border-border text-muted-foreground hover:bg-muted'
              }`}
              style={{ fontWeight: page === i + 1 ? 600 : 400 }}
            >
              {i + 1}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="p-1.5 rounded-lg border border-border text-muted-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

function RecordActions({ record }: { record: AttendanceRecord }) {
  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all duration-150"
        title="View full record details"
      >
        <Eye size={14} />
      </button>
      <button
        type="button"
        className="p-1.5 rounded-lg text-muted-foreground hover:text-amber-600 hover:bg-amber-50 transition-all duration-150"
        title={`Edit record for ${record.teacherName} on ${record.date}`}
      >
        <Edit2 size={14} />
      </button>
      <button
        type="button"
        className="p-1.5 rounded-lg text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-all duration-150"
        title="Delete this record"
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}